import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Search, Sparkles, ShieldCheck, AlertTriangle, Gem, Eye, ArrowLeft,
  Anchor, ExternalLink, Loader2, Layers,
} from "lucide-react";
import { toast } from "sonner";
import { obsidianDeepDive, listObsidianDeepDives } from "../lib/api";

function SeverityBadge({ severity }) {
  const colors = {
    low: "border-zinc-700 text-zinc-400",
    medium: "border-amber-500/60 text-amber-300",
    high: "border-rose-500 text-rose-300",
  };
  return (
    <span
      className={`text-[9px] uppercase tracking-widest px-1.5 py-0.5 border ${
        colors[severity] || colors.medium
      }`}
    >
      {severity}
    </span>
  );
}

function ProjectiveSurvival({ score }) {
  const pct = Math.round((score || 0) * 100);
  return (
    <div className="flex items-center gap-2">
      <div className="h-1 w-24 bg-zinc-900">
        <div className="h-full bg-fuchsia-500" style={{ width: `${pct}%` }} />
      </div>
      <span className="font-mono text-[10px] text-fuchsia-300">{pct}%</span>
    </div>
  );
}

export function ReportView({ report }) {
  if (!report) return null;
  return (
    <div className="space-y-6" data-testid="obsidian-report">
      {/* Anchored facts */}
      {report.anchored_facts?.length > 0 && (
        <section className="border border-emerald-500/30 bg-emerald-500/5 p-5">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-emerald-300 mb-3">
            <ShieldCheck className="h-3 w-3" /> Anchored facts · survived projection
          </div>
          <ul className="space-y-2">
            {report.anchored_facts.map((f, i) => (
              <li key={i} className="text-sm text-zinc-100" data-testid={`anchored-${i}`}>
                <div className="flex items-start gap-2">
                  <div className="text-emerald-400 font-mono text-xs mt-0.5">
                    {Math.round((f.confidence || 0) * 100)}%
                  </div>
                  <div className="flex-1">
                    <div>{f.claim}</div>
                    {f.reasoning && (
                      <div className="text-[11px] text-zinc-500 mt-0.5 italic">{f.reasoning}</div>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Anomalies */}
      {report.anomalies?.length > 0 && (
        <section className="border border-amber-500/40 bg-amber-500/5 p-5">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-amber-300 mb-3">
            <AlertTriangle className="h-3 w-3" /> Anomalies · mainstream ≠ evidence
          </div>
          <ul className="space-y-4">
            {report.anomalies.map((a, i) => (
              <li key={i} className="text-sm" data-testid={`anomaly-${i}`}>
                <div className="flex items-center gap-2 mb-1">
                  <SeverityBadge severity={a.severity} />
                  <span className="text-zinc-200">{a.claim}</span>
                </div>
                <div className="text-[11px] text-zinc-500 grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2 pl-4 border-l border-amber-500/20">
                  <div>
                    <div className="uppercase tracking-widest text-[9px] text-zinc-600 mb-1">
                      Mainstream
                    </div>
                    <div className="text-zinc-400">{a.mainstream_says}</div>
                  </div>
                  <div>
                    <div className="uppercase tracking-widest text-[9px] text-amber-400 mb-1">
                      Evidence
                    </div>
                    <div className="text-amber-200">{a.evidence_says}</div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Buried truth candidates */}
      {report.buried_truth_candidates?.length > 0 && (
        <section className="border border-fuchsia-500/40 bg-fuchsia-500/5 p-5">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-fuchsia-300 mb-3">
            <Gem className="h-3 w-3" /> Buried truth candidates
          </div>
          <ul className="space-y-4">
            {report.buried_truth_candidates.map((b, i) => (
              <li key={i} className="text-sm" data-testid={`buried-${i}`}>
                <div className="text-zinc-100 mb-1">{b.claim}</div>
                <div className="text-[11px] text-zinc-500 italic mb-2">
                  Why buried: {b.why_buried}
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-[9px] uppercase tracking-widest text-zinc-600">
                    verifiability: <span className="text-fuchsia-300">{b.verifiability}</span>
                  </span>
                  <ProjectiveSurvival score={b.projective_survival} />
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Sunk */}
      {report.sunk_to_obsidian?.length > 0 && (
        <section className="border border-zinc-800 bg-[#0b0b0d] p-5">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-3">
            <Layers className="h-3 w-3" /> Sunk to obsidian · did not survive
          </div>
          <ul className="space-y-1 text-xs text-zinc-500">
            {report.sunk_to_obsidian.map((s, i) => (
              <li key={i} data-testid={`sunk-${i}`}>· {s.claim}</li>
            ))}
          </ul>
        </section>
      )}

      {/* Provenance */}
      <div className="text-[10px] uppercase tracking-widest text-zinc-600 font-mono border-t border-zinc-900 pt-3">
        <div>report id · {report.id}</div>
        <div>hmac · {report.report_hash?.slice(0, 32)}…</div>
        <div>projected {report.subclaim_count} sub-claims in {(report.elapsed_ms / 1000).toFixed(1)}s</div>
      </div>
    </div>
  );
}

export default function Obsidian() {
  const [topic, setTopic] = useState("");
  const [running, setRunning] = useState(false);
  const [report, setReport] = useState(null);
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    listObsidianDeepDives(10)
      .then((r) => setRecent(r?.reports || []))
      .catch(() => {});
  }, [report]);

  const runDive = async () => {
    const t = topic.trim();
    if (t.length < 5) {
      toast.error("Give the Sink something concrete to work with.");
      return;
    }
    setRunning(true);
    setReport(null);
    try {
      const res = await obsidianDeepDive(t, 6);
      setReport(res);
      toast.success(
        `Deep-dive sealed · ${res.anchored_facts?.length || 0} anchored · ` +
        `${res.anomalies?.length || 0} anomalies · ` +
        `${res.buried_truth_candidates?.length || 0} buried candidates`
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Deep-dive failed");
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#09090b] text-zinc-100">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
        <Link
          to="/"
          data-testid="obsidian-back"
          className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-zinc-500 hover:text-emerald-300 mb-6"
        >
          <ArrowLeft className="h-3 w-3" /> home
        </Link>

        <div className="border-b border-zinc-900 pb-5 mb-6">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.4em] text-fuchsia-300 mb-2">
            <Eye className="h-3 w-3" /> Obsidian Sink · deep-dive engine
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl tracking-[0.05em]">
            Hunt for buried truths.
          </h1>
          <p className="text-zinc-400 text-sm mt-3 max-w-3xl leading-relaxed">
            Give the Sink a historical topic. It decomposes the topic into
            atomic sub-claims, projects each through Quick Verdict, detects
            where the mainstream telling diverges from the evidence, and
            surfaces facts that appear in verified sources but were
            conspicuously dropped from the accepted narrative. Every report
            is HMAC-signed. Every anchored fact feeds the neuro-symbolic
            library. Every dropped claim is logged for audit.
          </p>
        </div>

        {/* Composer */}
        <div className="border border-zinc-800 bg-[#0b0b0d] p-4 mb-6">
          <div className="flex flex-col sm:flex-row gap-2">
            <input
              type="text"
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              placeholder="e.g. the burning of the Library of Alexandria · the origin of the 1918 flu · who funded the Manhattan Project"
              disabled={running}
              data-testid="obsidian-topic-input"
              className="flex-1 bg-zinc-950 border border-zinc-800 focus:border-fuchsia-500 outline-none px-3 py-2 text-sm font-mono text-zinc-100 placeholder-zinc-600"
              onKeyDown={(e) => e.key === "Enter" && runDive()}
            />
            <button
              onClick={runDive}
              disabled={running}
              data-testid="obsidian-run-btn"
              className="inline-flex items-center justify-center gap-2 px-5 py-2 border border-fuchsia-500 text-fuchsia-300 hover:bg-fuchsia-500 hover:text-zinc-950 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-[11px] uppercase tracking-[0.25em]"
            >
              {running ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 animate-spin" /> Projecting…
                </>
              ) : (
                <>
                  <Search className="h-3.5 w-3.5" /> Sink
                </>
              )}
            </button>
          </div>
          <p className="text-[10px] text-zinc-600 mt-2 uppercase tracking-widest">
            30-60s per dive · multi-model triangulation · results are permanent
          </p>
        </div>

        {/* Result or loading state */}
        {running && (
          <div className="border border-fuchsia-500/30 bg-[#0b0b0d] p-8 text-center mb-6">
            <Sparkles className="h-6 w-6 text-fuchsia-300 animate-pulse inline-block mb-2" />
            <div className="text-fuchsia-300 text-[11px] uppercase tracking-widest">
              Enumerating sub-claims · projecting · detecting anomalies · surfacing buried facts…
            </div>
          </div>
        )}
        {report && <ReportView report={report} />}

        {/* Recent dives */}
        {recent.length > 0 && (
          <section className="mt-10">
            <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-3">
              Recent deep-dives · public
            </div>
            <ul className="space-y-1 border border-zinc-800 bg-[#0b0b0d]">
              {recent.map((r) => (
                <Link
                  to={`/obsidian/report/${r.id}`}
                  key={r.id}
                  data-testid={`recent-dive-${r.id}`}
                  className="flex items-center gap-3 px-4 py-2 border-b border-zinc-900 last:border-b-0 text-sm hover:bg-zinc-950/60 transition-colors"
                >
                  <Anchor className="h-3 w-3 text-zinc-600 shrink-0" />
                  <div className="flex-1 truncate text-zinc-200">{r.topic}</div>
                  <div className="text-[10px] text-zinc-500 font-mono hidden sm:block">
                    {r.anchored_count} anchored · {r.anomaly_count} anomalies · {r.buried_count} buried
                  </div>
                  <div className="text-[9px] text-zinc-600 font-mono">
                    {r.user_handle ? "@" + r.user_handle : "anon"}
                  </div>
                </Link>
              ))}
            </ul>
          </section>
        )}

        <div className="text-center mt-10">
          <Link
            to="/knowledge-graph"
            data-testid="obsidian-to-graph"
            className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-zinc-500 hover:text-fuchsia-300"
          >
            <ExternalLink className="h-3 w-3" /> Browse the neuro-symbolic library →
          </Link>
        </div>
      </div>
    </div>
  );
}
